/* línkè · 跨设备状态同步
   - monkey-patch localStorage.setItem / removeItem，对 SYNCED_KEYS 的写入 debounced 推到 /api/state
   - 启动时 pullFromServer() 把远端 state 拉下来覆盖本地（不触发回推循环）
   - enableSync() 之后才真正推送（避免 pull 阶段产生干扰）

   依赖：浏览器 + cookie 同源到 lingke.bond（dev 走 vite proxy） */

import { SCOPED_SYNC_KEYS, THEME_MANAGED_KEYS, SEEDED_FLAG } from './themeSkin.js';

const ENDPOINT = '/api/state';
const PUSH_DEBOUNCE_MS = 400;

/* 哪些 localStorage key 走云同步。
   - provider 配置（API key 等）故意不同步——属于"这台设备的偏好"、且含密钥
   - legacy lingke.chat.system 不同步——已被迁移到角色卡
   - lingke.chat.messages.v1 不在这里——聊天消息走 /api/messages 直接读写 server，
     不依赖 localStorage 缓存（避免 React mount 时机引发的同步竞态）
   - lingke.diary.draft / lingke.tarot.handoff 不同步——是「正在进行中/一次性」的临时态

   2026-07-30 铃清了浏览器缓存后壁纸/主题/头像全丢（这些只存本地、服务器没底），
   把「视觉个性化」也纳入云同步：以后清缓存、换手机、换浏览器都能自己回来。 */
const SYNCED_KEYS = new Set([
  // —— 原有：角色卡 + 预设（走 React state，拉下来即时生效，不需重刷）——
  'lingke.cc.characters',
  'lingke.cc.active_character',
  'lingke.preset.list',
  'lingke.preset.active',
  'lingke.lb.entries',
  // —— 新增：视觉个性化 ——
  'lingke.theme',
  'lingke.accent',
  'lingke.bg',
  'lingke.textColor',
  'lingke.userBubble',
  'lingke.bubbleSolid',
  // ⚠️ lingke.skin.seeded.v1（「主题已分家」标记）故意不同步：它是每台设备
  //    自己的一次性初始化标记。曾把它同步过——清缓存的设备柜子(@主题 key)全空、
  //    标记却被云端拉回 → ensureSeeded 不再分家 → 四主题串成一套（2026-07-30）。
  'lingke.pearls.lang',
  'lingke.paper.choice.v1', // 她给某篇日记/记忆亲手换的纸（10-05）
  'lingke.avatar.user.v1',
  'lingke.avatar.sael.v1',
  // 聊天壁纸（img 是大头 base64，其余是薄薄的开关/浓度/位置）
  'lingke.chat.wallpaper.img.v1',
  'lingke.chat.wallpaper.dim.v1',
  'lingke.chat.wallpaper.blur.v1',
  'lingke.chat.wallpaper.pos.v1',
  'lingke.chat.wallpaper.top.v1',
  'lingke.chat.wallpaper.toprgb.v1',
  // 首页壁纸
  'lingke.home.wallpaper.img.v1',
  'lingke.home.wallpaper.dim.v1',
  'lingke.home.wallpaper.blur.v1',
  'lingke.home.wallpaper.pos.v1',
]);

/* 「柜子」也要备份（2026-07-30）：themeSkin.js 把非当前主题的整套外观寄存在
   `原key@主题id` 里。只备份「身上穿的」不备份柜子的话，清缓存恢复后柜子全空、
   切主题没得换 → 一套行头串遍四主题。柜子 key 全集由 themeSkin 生成（60 个）。 */
const SCOPED_SYNCED = new Set(SCOPED_SYNC_KEYS);
/* 「身上穿的」主题相关 key（真身备份在柜子里，服务器上这层可放心镜像本设备） */
const THEME_MANAGED = new Set(THEME_MANAGED_KEYS);

/* 这些 key 是「开机时命令式应用」的（主题/强调色/壁纸/头像……见 main.jsx），
   不像角色卡走 React state 拉下来就即时重渲染。所以一旦从服务器拉到跟本地不同的值
   （典型：清过缓存的设备本地是空的、服务器有底），需要「刷新一次」让开机代码带着
   正确的值重新应用一遍，否则会停在默认主题/没有壁纸。判定见 pullFromServer。 */
const VISUAL_BOOT_KEYS = new Set([
  'lingke.theme',
  'lingke.accent',
  'lingke.bg',
  'lingke.textColor',
  'lingke.userBubble',
  'lingke.bubbleSolid',
  'lingke.avatar.user.v1',
  'lingke.avatar.sael.v1',
  'lingke.chat.wallpaper.img.v1',
  'lingke.chat.wallpaper.dim.v1',
  'lingke.chat.wallpaper.blur.v1',
  'lingke.chat.wallpaper.pos.v1',
  'lingke.chat.wallpaper.top.v1',
  'lingke.chat.wallpaper.toprgb.v1',
  'lingke.home.wallpaper.img.v1',
  'lingke.home.wallpaper.dim.v1',
  'lingke.home.wallpaper.blur.v1',
  'lingke.home.wallpaper.pos.v1',
]);
function isVisualBoot(key) {
  return VISUAL_BOOT_KEYS.has(key);
}

const pending = new Map(); // key -> raw string value
/* lastSyncedRaw: 我们认为的"服务器当前值"——来自 pull 或上次 push 成功的 raw string。
   queuePush 拿到的值若跟它一致就跳过推送（防 React mount 的 useEffect 把同值再写一遍
   触发无意义的 push，导致覆盖云端真实数据）。
   ⚠️ 曾有个 justPulled Set 想「防 pull 写入被回推」，但 pull 用 rawSet（原生写、绕过
   patch）根本不触发 patched setItem，justPulled 永远轮不到被消费 → 反而把用户【改主题后
   第一次写入】误吞、不推服务器 → 重载后被服务器旧值打回（2026-07-30 铃换主题总跳回粉色的
   真凶）。已删除：同值去重交给 lastSyncedRaw 就够，不需要 justPulled。 */
const lastSyncedRaw = new Map();
let pushTimer = null;
let syncEnabled = false;

/* 原生 setItem/removeItem 的引用，供 pull 绕过 patch 直接写本地。
   ⚠️ 必须放在【模块作用域变量】里，绝不能挂到 localStorage 上（曾经的写法
   `localStorage.__lingkeRawSet = fn` 在 Safari/WebKit 上会被 Storage 的具名属性
   setter 把函数【强转成字符串】存进去，取出来一调用就报 "is not a function"，
   导致 pull 整个抛错→同步被判失败停用→怎么改都不推送。2026-07-30 铃在 iOS Safari
   上坐实了这个坑，凶手就是这一条）。 */
let rawSet = null;
let rawRemove = null;
let patched = false;

function isSynced(key) {
  return SYNCED_KEYS.has(key) || SCOPED_SYNCED.has(key);
}

function buildEntries() {
  const now = new Date().toISOString();
  const entries = [];
  const rawByKey = new Map();
  for (const [key, rawValue] of pending) {
    let parsed = null;
    if (rawValue !== null) {
      try { parsed = JSON.parse(rawValue); } catch { parsed = rawValue; }
    }
    entries.push({ key, value: parsed, updated_at: now });
    rawByKey.set(key, rawValue);
  }
  pending.clear();
  return { entries, rawByKey };
}

async function flushPush() {
  pushTimer = null;
  if (!syncEnabled || pending.size === 0) return;
  const { entries, rawByKey } = buildEntries();
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries }),
    });
    if (!res.ok) {
      console.warn('[sync] push HTTP', res.status);
    } else {
      // 推成功——更新"已知服务器值"
      for (const [k, raw] of rawByKey) lastSyncedRaw.set(k, raw);
    }
  } catch (e) {
    console.warn('[sync] push failed', e.message);
    // 重新放回 pending，下次写入会带它一起重试
    for (const e2 of entries) pending.set(e2.key, JSON.stringify(e2.value));
  }
}

function queuePush(key, rawValue) {
  // 跟"已知服务器值"相同就不推——避免 React useEffect mount 时无意义的同值 setItem
  // 把云端真实数据覆盖（这是 v1 的核心 bug）
  if (lastSyncedRaw.get(key) === rawValue) return;
  pending.set(key, rawValue);
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(flushPush, PUSH_DEBOUNCE_MS);
}

/* 页面要走了（刷新/关闭/切走被杀）：pending 里还没到 400ms 防抖的推送会整个丢掉。
   铃就栽在这：切完主题立刻刷新 → 交接产生的写入没来得及推 → 服务器一直卡着旧主题那套。
   sendBeacon 专为「离场瞬间发一发」而生：同源自带 cookie、页面关了也保证送达。 */
function flushOnLeave() {
  if (!syncEnabled || pending.size === 0) return;
  const { entries, rawByKey } = buildEntries();
  try {
    const ok = navigator.sendBeacon(
      ENDPOINT,
      new Blob([JSON.stringify({ entries })], { type: 'application/json' }),
    );
    if (ok) for (const [k, raw] of rawByKey) lastSyncedRaw.set(k, raw);
  } catch { /* 发不出去也没辙了，下次开机种子上传会兜底 */ }
}
try {
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('pagehide', flushOnLeave);
  }
} catch {}

/* 安装 monkey-patch。多次调用幂等。 */
function installPatch() {
  if (patched) return;
  const origSet = localStorage.setItem.bind(localStorage);
  const origRemove = localStorage.removeItem.bind(localStorage);
  localStorage.setItem = function (key, value) {
    origSet(key, value);
    if (!isSynced(key)) return;
    queuePush(key, String(value));
  };
  localStorage.removeItem = function (key) {
    origRemove(key);
    if (!isSynced(key)) return;
    queuePush(key, null);
  };
  // 原生读写存到【模块变量】里供 pull 用（不能挂 localStorage，见上方 rawSet 说明）
  rawSet = origSet;
  rawRemove = origRemove;
  patched = true;
}

installPatch();

/* 从服务器拉取所有同步 state，覆盖本地 */
export async function pullFromServer() {
  try {
    const res = await fetch(ENDPOINT, { credentials: 'include' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json();
    if (!data || !data.ok || !data.state) return { pulled: 0, visualChanged: false };
    let pulled = 0;
    // 只在「本地空、靠服务器补回来」时才需要刷新一次，让开机命令式应用的视觉设定
    // （主题/壁纸/头像）带着恢复的值重新生效（见该文件顶部说明）。
    let visualChanged = false;

    /* 同步策略（2026-07-30 两轮教训后的定稿，改前先读）：
       ① 【本地优先】：这台设备上有值的 key，本地说了算，服务器绝不覆盖；
          与服务器不同就把本地推上去更新「底」（铃「换主题一刷新就跳回粉色」的根治）。
       ② 【只有整台设备是新的，才用服务器补】：判据 = 本地连 lingke.theme 都没有
          （清缓存/新设备会把所有 key 一起清掉）。此时才整套恢复（含柜子），并刷新一次应用。
          ⚠️ 不能按「单个 key 本地为空」来补——「初雪本来就没壁纸」是合法状态，
          按 key 补会把别的主题的壁纸硬塞回来（铃「粉色行头串遍四主题」的帮凶之一）。
       ③ 初始化过的设备上「本地没有、服务器有」的 key 两边都不动：本地的空是有含义的，
          推 null 上去又会把云端备份抹掉（另一台设备可能还指着它恢复）。真正的删除
          在用户操作那一刻就由 patched removeItem 推 null 了，不需要开机再补刀。 */
    let freshDevice = false;
    try {
      freshDevice = localStorage.getItem('lingke.theme') === null;
    } catch { freshDevice = false; }

    const serverRawOf = (entry) =>
      entry.value === null || entry.value === undefined
        ? null
        : typeof entry.value === 'string'
          ? entry.value
          : JSON.stringify(entry.value);

    for (const [key, entry] of Object.entries(data.state)) {
      if (!isSynced(key)) continue;
      const before = localStorage.getItem(key); // 原生 getItem，不受 patch 影响
      const serverRaw = serverRawOf(entry);

      if (before === null || before === undefined) {
        if (freshDevice && serverRaw !== null) {
          // 新设备：整套恢复
          rawSet(key, serverRaw);
          lastSyncedRaw.set(key, serverRaw);
          if (isVisualBoot(key)) visualChanged = true;
          pulled++;
          /* 云端恢复的设备直接立「已分家」标记：它拿到的已是分家后的完整真相
             （柜子空=那个主题就是用默认，是合法状态）。不立的话 ensureSeeded 会把
             恢复出来的当前行头复印进空柜子当「起点」→ 蓝行头串进橙/紫主题
             （2026-07-30 铃在重新添加的主屏幕 App 上坐实，粉色复印机变蓝色复印机）。 */
          if (key === 'lingke.theme') rawSet(SEEDED_FLAG, '1');
        } else if (
          serverRaw !== null &&
          (THEME_MANAGED.has(key) || SCOPED_SYNCED.has(key))
        ) {
          /* 初始化过的设备 + 主题相关 key：本地的空（=跟随主题/该主题没这件）要
             【镜像到服务器】——推 null 把旧值清掉。否则服务器上会永远压着过时的
             旧配饰（如早晨粉主题时代的 accent），下一台新设备/清缓存恢复时整套
             穿回来 → 「蓝主题披粉配饰」（2026-07-30 铃在新加的主屏幕 App 上坐实）。
             敢这么清是因为真正的备份在柜子(@主题 key)里，这层只是「当前穿着」。 */
          lastSyncedRaw.set(key, serverRaw); // 声明服务器现值 → queuePush(null) 会真的推
          queuePush(key, null);
        } else {
          // 非主题类 key（头像/角色卡等）：本地的空不动服务器——云端那份可能是
          // 别的设备的备份，推 null 会把它抹掉（见 ③）
          lastSyncedRaw.set(key, serverRaw);
        }
      } else {
        // 本地有值：本地优先，不覆盖；不同就把本地推上去更新备份
        if (serverRaw !== before) {
          lastSyncedRaw.set(key, serverRaw); // 让 queuePush 认为服务器是旧值 → 真的推
          queuePush(key, before);
        } else {
          lastSyncedRaw.set(key, before); // 一致，别重复推
        }
      }
    }

    // 服务器完全没返回、但白名单里的 key（含柜子）：本地有值就种子上传备份。
    for (const key of [...SYNCED_KEYS, ...SCOPED_SYNCED]) {
      if (lastSyncedRaw.has(key)) continue; // 服务器已返回、上面处理过了
      let localRaw = null;
      try { localRaw = localStorage.getItem(key); } catch { localRaw = null; }
      if (localRaw !== null && localRaw !== undefined) {
        lastSyncedRaw.set(key, null);   // 让 queuePush 认为「云端还没有」，从而真的推上去
        queuePush(key, localRaw);
      } else {
        lastSyncedRaw.set(key, null);
      }
    }
    return { pulled, visualChanged };
  } catch (e) {
    console.warn('[sync] pull failed', e.message);
    return { error: e.message };
  }
}

/* 在 pull 完成后调用，开启后续 setItem 自动推送 */
export function enableSync() {
  syncEnabled = true;
  // pull 阶段种子上传排进了 pending，但那会儿 syncEnabled 还是 false、flush 会空转。
  // 这里开了闸就补一次 flush，把种子（本地已有、云端没有的设置）真正发出去。
  if (pending.size > 0 && !pushTimer) {
    pushTimer = setTimeout(flushPush, PUSH_DEBOUNCE_MS);
  }
}

/* pull 含 timeout 保险——网慢就用本地数据先工作 */
export async function pullFromServerWithTimeout(ms = 3500) {
  return Promise.race([
    pullFromServer(),
    new Promise((r) => setTimeout(() => r({ timeout: true }), ms)),
  ]);
}

/* 测试 / 调试用 */
export function _internals() {
  return {
    syncEnabled,
    pendingSize: pending.size,
    syncedKeys: [...SYNCED_KEYS],
    scopedKeys: [...SCOPED_SYNCED],
  };
}
